import { isCityCommand } from "../cityCommand/cityVerbs";
import { without } from "../fields";
import { type AxialCoord, hexDistance, hexEquals, hexNeighbors } from "../hex";
import {
  cityTooClose,
  isWorkingCityOf,
  ownWorkingCity,
  structureComplete,
  workingCities,
} from "../land/cities";
import { nearestTo } from "../pick";
import { findHex, ownGround, patchHexAt } from "../run/board";
import { isMoveTick, isProductionTick } from "../run/cadence";
import { addSupply, kingOf, spend, supplyOf, withKing } from "../run/pools";
import { sideOf } from "../run/sides";
import type { RunRules } from "../types/rules";
import type {
  CommandKind,
  CommandRefusal,
  FactionId,
  HexState,
  King,
  Role,
  RunState,
} from "../types/state";
import { walk } from "../units/routes";
import { commandersOf } from "./commanders";

/**
 * The King and his court (ADR-0011): Trust, Will and its refill, reach, the throne, every command's
 * price and refusal, his walk, founding a city and his flight.
 */

/** The capital still stands: the faction holds its starting city and it works. */
export function capitalStands(state: RunState, faction: FactionId): boolean {
  const king = kingOf(state, faction);
  return isWorkingCityOf(king && findHex(state, king.capital), faction, state.tick);
}

/**
 * Trust, the size of the Will pool: the base, the capital while it stands, a pip per other
 * working city (capped at `trustMax`), less the flights, a pip per mayor in office and a pip
 * per commander alive, never below 1. 0 without a court.
 */
export function trust(state: RunState, faction: FactionId): number {
  const court = state.rules.court;
  const king = kingOf(state, faction);
  if (!court || !king) {
    return 0;
  }
  const capital = capitalStands(state, faction);
  const otherCities = workingCities(state, faction).length - (capital ? 1 : 0);
  const earned =
    court.trustBase + (capital ? court.trustCapital : 0) + otherCities * court.trustPerCity;
  return Math.max(
    1,
    Math.min(court.trustMax, earned) -
      king.flights * court.flightPenalty -
      mayoredCities(state, faction).length -
      commandersOf(state, faction).length,
  );
}

/** The faction's cities run by a mayor; each holds a pip of its King's Trust. */
export function mayoredCities(state: RunState, faction: FactionId): HexState[] {
  return workingCities(state, faction).filter((city) => city.mayor);
}

/** How far the King commands: wider once the faction is a kingdom and not a town. 0 without a court. */
export function kingReach(state: RunState, faction: FactionId): number {
  const court = state.rules.court;
  if (!court) {
    return 0;
  }
  return workingCities(state, faction).length >= court.kingReachCities
    ? court.kingReachGrown
    : court.kingReach;
}

/** True when the faction's King commands this hex; always true without a court. */
export function inKingReach(state: RunState, faction: FactionId, coord: AxialCoord): boolean {
  const king = kingOf(state, faction);
  return !king || hexDistance(king.coord, coord) <= kingReach(state, faction);
}

/** Will a command costs; 0 for a stop and for everything without a court. */
export function commandCost(rules: RunRules, kind: CommandKind): number {
  return rules.court && kind !== "stop" && kind !== "dismiss" && kind !== "demote"
    ? rules.court.costs[kind]
    : 0;
}

/**
 * Court commands choose who rules: from the standing capital's hex they carry kingdom-wide.
 * Appointing is not one since A32: the King hands the office to a soldier within his reach.
 */
function isCourtCommand(kind: CommandKind): boolean {
  return kind === "dismiss" || kind === "aim" || kind === "stance";
}

/** The call and the grants: given from the throne only, and then kingdom-wide. */
function isGeneralCommand(kind: CommandKind): boolean {
  return kind === "call" || kind === "tax" || kind === "rush";
}

/**
 * The King sits where a general sits: on his standing capital, or, once that has fallen, on
 * any own working city.
 */
export function onThrone(state: RunState, faction: FactionId): boolean {
  const king = kingOf(state, faction);
  if (!king) {
    return false;
  }
  if (capitalStands(state, faction)) {
    return hexEquals(king.coord, king.capital);
  }
  return ownWorkingCity(state, faction, king.coord) !== undefined;
}

function kingOnCapital(state: RunState, faction: FactionId): boolean {
  const king = kingOf(state, faction);
  return king !== undefined && hexEquals(king.coord, king.capital) && capitalStands(state, faction);
}

/**
 * The court's verdict on a command at `coord`: out of the King's reach, or short of Will;
 * undefined when the court lets it through (always, without a court, and always for a
 * delegate). Reach is named first: it is what the player can fix by walking. A court command
 * is in reach anywhere while the King stands on his capital. Appointing and promoting also
 * need a pip of Trust to hand over: at the last pip they are refused as short of Will.
 */
export function commandRefusal(
  state: RunState,
  faction: FactionId,
  coord: AxialCoord,
  kind: CommandKind,
  issuer: Role = "king",
): CommandRefusal | undefined {
  const king = kingOf(state, faction);
  // CC1: a city-command King has no court to answer to.
  if (!king || issuer !== "king" || isCityCommand(state, faction)) {
    return undefined;
  }
  if (isGeneralCommand(kind)) {
    if (!onThrone(state, faction)) {
      return "reach";
    }
  } else if (kind === "mark") {
    // A banner carries further than the King's hand, from wherever he stands (A39).
    if (hexDistance(king.coord, coord) > (state.rules.court?.general.markRange ?? 0)) {
      return "reach";
    }
  } else {
    const carries = isCourtCommand(kind) && kingOnCapital(state, faction);
    if (!carries && !inKingReach(state, faction, coord)) {
      return "reach";
    }
  }
  if ((kind === "appoint" || kind === "promote") && trust(state, faction) <= 1) {
    return "will";
  }
  return king.will < commandCost(state.rules, kind) ? "will" : undefined;
}

/** Takes a command's Will from the pool. The caller has already asked `commandRefusal`. */
export function spendWill(
  state: RunState,
  faction: FactionId,
  kind: CommandKind,
  issuer: Role = "king",
): RunState {
  const king = kingOf(state, faction);
  const cost =
    issuer === "king" && !isCityCommand(state, faction) ? commandCost(state.rules, kind) : 0;
  return king && cost > 0 ? withKing(state, faction, { ...king, will: king.will - cost }) : state;
}

/** Own, unrazed land, and the neutral site of his founding errand (CC1): the ground a King walks. */
function kingGround(state: RunState, faction: FactionId): HexState[] {
  const site = kingOf(state, faction)?.founds;
  if (!site) {
    return ownGround(state, faction);
  }
  return state.hexes.filter(
    (hex) =>
      !hex.razed && (hex.owner === faction || (hex.owner === "none" && hexEquals(hex.coord, site))),
  );
}

/** The King's road to `to` over own land only (and his errand's site, CC1); undefined when there is none. */
export function routeKing(
  state: RunState,
  faction: FactionId,
  to: AxialCoord,
): AxialCoord[] | undefined {
  const king = kingOf(state, faction);
  return (
    king &&
    walk(kingGround(state, faction), sideOf(state.rules, faction), king.coord, to, new Set(), false)
  );
}

/**
 * Send the King somewhere on own land: it walks one hex per move step, for no Will. The same
 * destination again is a no-op; rejected (same object) when the run is over, there is no
 * court or no road over own land. A ride anywhere but his founding site drops the errand and
 * gives its price back (CC1).
 */
export function moveKing(state: RunState, faction: FactionId, to: AxialCoord): RunState {
  const king = kingOf(state, faction);
  if (state.status !== "running" || !king) {
    return state;
  }
  if (king.destination && hexEquals(king.destination, to)) {
    return state;
  }
  const free = king.founds ? dropErrand(state, faction) : state;
  const rider = kingOf(free, faction) ?? king;
  return routeKing(free, faction, to)
    ? withKing(free, faction, { ...rider, destination: to })
    : state;
}

/** The King stops where it stands, his founding errand dropped and paid back (CC1). Same object back when it was already still. */
export function stopKing(state: RunState, faction: FactionId): RunState {
  const king = kingOf(state, faction);
  if (king?.founds) {
    return dropErrand(state, faction);
  }
  if (!king || king.destination === undefined) {
    return state;
  }
  const standing = without(king, "destination");
  return withKing(state, faction, standing);
}

/**
 * Where a city-command King may found (CC1): a hex with no structure, unrazed, no city of anyone
 * within `cityMinSpacing`, on own land or, with `frontierFounding`, a neutral wall-free hex
 * touching own land. Price aside.
 */
export function isFoundingSite(state: RunState, faction: FactionId, coord: AxialCoord): boolean {
  const rules = state.rules.cityCommand;
  const hex = findHex(state, coord);
  if (!rules || !hex || hex.razed || hex.structure !== "none") {
    return false;
  }
  const placed =
    hex.owner === faction ||
    (rules.frontierFounding &&
      hex.owner === "none" &&
      hex.walls === 0 &&
      hexNeighbors(coord).some((neighbour) => findHex(state, neighbour)?.owner === faction));
  // The spacing last: it reads every city of the map, and the script asks it of every hex (war-speed).
  return placed && !cityTooClose(state, coord);
}

/** The King's founding errand dropped, its price back in the pool and his ride with it (CC1); same object without one. */
function dropErrand(state: RunState, faction: FactionId): RunState {
  const king = kingOf(state, faction);
  if (!king?.founds) {
    return state;
  }
  const idle = without(king, "founds", "destination");
  return withKing(addSupply(state, faction, state.rules.cityCost), faction, idle);
}

/** The founding itself (CC1): the site becomes the faction's and a city starts on it, its price already paid. */
function raiseCity(state: RunState, faction: FactionId, at: AxialCoord): RunState {
  return patchHexAt(state, at, {
    owner: faction,
    structure: "city",
    structureReadyTick: state.tick + state.rules.cityBuildTicks,
  });
}

/**
 * Only a city-command King founds (CC1), and in person: the site must be one (`isFoundingSite`)
 * and the pool must hold `cityCost`, which is paid now; he rides there and founds on arrival
 * (`moveKings`). Standing on it, he founds at once. A new errand replaces the old one, whose
 * price comes back first. Same object back when refused or already riding to this site.
 */
export function sendToFound(state: RunState, faction: FactionId, at: AxialCoord): RunState {
  const king = kingOf(state, faction);
  if (state.status !== "running" || !king || !isCityCommand(state, faction)) {
    return state;
  }
  if ((king.founds && hexEquals(king.founds, at)) || !isFoundingSite(state, faction, at)) {
    return state;
  }
  const free = dropErrand(state, faction);
  if (supplyOf(free, faction) < state.rules.cityCost) {
    return state;
  }
  const paid = spend(free, faction, state.rules.cityCost);
  const rider = kingOf(paid, faction) ?? king;
  if (hexEquals(rider.coord, at)) {
    const still = without(rider, "destination");
    return raiseCity(withKing(paid, faction, still), faction, at);
  }
  const sent = withKing(paid, faction, { ...rider, destination: at, founds: at });
  return routeKing(sent, faction, at) ? sent : state;
}

/**
 * Step 1, on the production interval: one Will every `refillIntervals` intervals by where the
 * King stands (the standing capital, another own working city, the field), and the pool is
 * cut to Trust when Trust has dropped under it.
 */
export function refillWill(state: RunState): RunState {
  const court = state.rules.court;
  if (!court || !state.kings || !isProductionTick(state)) {
    return state;
  }
  const interval = state.tick / state.rules.productionIntervalTicks;
  let next = state;
  for (const faction of state.factions) {
    const king = state.kings[faction];
    // CC1: a city-command King spends no Will, so his pool stands still.
    if (!king || isCityCommand(state, faction)) {
      continue;
    }
    const here = findHex(state, king.coord);
    const onCity =
      here !== undefined &&
      here.owner === faction &&
      here.structure === "city" &&
      structureComplete(here, state.tick);
    const every =
      onCity && hexEquals(king.coord, king.capital)
        ? court.refillIntervals.capital
        : onCity
          ? court.refillIntervals.city
          : court.refillIntervals.field;
    const gain = interval % every === 0 ? 1 : 0;
    const will = Math.min(trust(state, faction), king.will + gain);
    if (will !== king.will) {
      next = withKing(next, faction, { ...king, will });
    }
  }
  return next;
}

/**
 * The court flees when the hex under the King is no longer its faction's: to the capital
 * while it stands, else the nearest own working city, else the nearest own hex. Will drops to
 * 0 and the flight is counted against Trust for the rest of the run. With no own hex left the
 * King stays; the faction is eliminated in the same tick. A city-command King (CC1) keeps his
 * Will and Trust and is shaken for `shakenTicks` instead; a founding errand ends, paid back.
 */
export function flee(state: RunState): RunState {
  if (!state.kings) {
    return state;
  }
  let next = state;
  for (const faction of state.factions) {
    const king = state.kings[faction];
    if (!king) {
      continue;
    }
    if (findHex(state, king.coord)?.owner === faction) {
      continue;
    }
    const refuge = capitalStands(state, faction)
      ? findHex(state, king.capital)
      : (nearestTo(workingCities(state, faction), king.coord) ??
        nearestTo(ownGround(state, faction), king.coord));
    if (!refuge) {
      continue;
    }
    next = dropErrand(next, faction);
    const court = without(kingOf(next, faction) ?? king, "destination");
    const shaken = state.rules.cityCommand;
    next = withKing(
      next,
      faction,
      shaken && isCityCommand(state, faction)
        ? {
            ...court,
            coord: refuge.coord,
            movedTick: state.tick,
            shakenUntil: state.tick + shaken.shakenTicks,
          }
        : {
            ...court,
            coord: refuge.coord,
            movedTick: state.tick,
            will: 0,
            flights: king.flights + 1,
          },
    );
  }
  return next;
}

/**
 * Step 3, on the move interval: each King with a destination takes one hex along own land, a
 * city-command King `kingStepHexes` (CC1); one that is cut off waits.
 */
export function moveKings(state: RunState): RunState {
  if (!state.kings || !isMoveTick(state)) {
    return state;
  }
  let next = state;
  for (const faction of state.factions) {
    if (!state.kings[faction]) {
      continue;
    }
    const steps = isCityCommand(state, faction) ? (state.rules.cityCommand?.kingStepHexes ?? 1) : 1;
    for (let step = 0; step < steps; step += 1) {
      const before = next;
      next = kingStep(next, faction);
      if (next === before) {
        break;
      }
    }
  }
  return next;
}

/**
 * One hex of a King's walk. A founding errand whose site is no site any more is dropped and paid
 * back; one whose site he steps onto founds the city there (CC1).
 */
function kingStep(state: RunState, faction: FactionId): RunState {
  const king = kingOf(state, faction);
  if (!king?.destination) {
    return state;
  }
  if (king.founds && !isFoundingSite(state, faction, king.founds)) {
    return dropErrand(state, faction);
  }
  const step = routeKing(state, faction, king.destination)?.[1];
  if (!step) {
    return state;
  }
  const { destination, founds: site, ...rest } = king;
  const moved: King = { ...rest, coord: step, movedTick: state.tick };
  if (!hexEquals(step, destination)) {
    return withKing(
      state,
      faction,
      site ? { ...moved, destination, founds: site } : { ...moved, destination },
    );
  }
  const arrived = withKing(state, faction, moved);
  return site && hexEquals(site, step) ? raiseCity(arrived, faction, site) : arrived;
}
